import { useId, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, ReactElement } from 'react';
import { Icon } from './Icon';

/**
 * Checks a file against an `<input accept>`-style pattern list: comma
 * separated `.ext`, `type/*` and `type/subtype` entries. An empty pattern
 * list means "no restriction", matching the native `accept` attribute.
 */
function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept
    .split(',')
    .map((pattern) => pattern.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;

  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) return name.endsWith(pattern);
    if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1));
    return type === pattern;
  });
}

/**
 * A drop target that is also a real file input, used by the CSV upload
 * dialog and the logo upload (spec §9.4).
 *
 * The input stays in the document and reachable by keyboard and screen
 * reader — it is hidden with Tailwind's `sr-only` clipping technique, never
 * `display: none`, which would pull it out of the tab order entirely. Its
 * accessible name is built from two `aria-labelledby` targets: a visually
 * hidden span holding `label` (e.g. "CSV file") and the visible "Choose a
 * file" span, so the name always contains the visible text (WCAG 2.5.3
 * Label in Name) while still describing what the file is for.
 *
 * `accept` is enforced on both paths: the browser filters the native file
 * picker, but nothing stops a drop, so a dropped file that doesn't match
 * `accept` is rejected here too and reported through `onReject` instead of
 * silently reaching `onFile`.
 */
export function DropZone(props: {
  accept: string;
  label: string;
  onFile(file: File): void;
  onReject?(file: File): void;
  disabled?: boolean;
}): ReactElement {
  const id = useId();
  const labelId = `${id}-label`;
  const textId = `${id}-text`;
  const [isDragging, setIsDragging] = useState(false);
  // A drag-depth counter, rather than a plain boolean, so moving the pointer
  // from the label onto a child (the icon or the text) doesn't flicker the
  // active state off: the browser fires dragenter on the child and dragleave
  // on the label for that same pointer move, even though the pointer never
  // left the drop zone.
  const dragDepth = useRef(0);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still fires a change event.
    event.target.value = '';
    if (file) props.onFile(file);
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>): void {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (props.disabled) return;
    // A multi-file drop takes the first file by design.
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!matchesAccept(file, props.accept)) {
      props.onReject?.(file);
      return;
    }
    props.onFile(file);
  }

  function handleDragEnter(event: DragEvent<HTMLLabelElement>): void {
    event.preventDefault();
    if (props.disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>): void {
    // Required so the element is a valid drop target; the active state
    // itself is driven by the dragenter/dragleave depth counter below.
    event.preventDefault();
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>): void {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }

  return (
    <label
      htmlFor={id}
      data-dragging={isDragging ? 'true' : undefined}
      data-disabled={props.disabled ? 'true' : undefined}
      className={`flex items-center justify-center gap-2 px-4 py-5 border border-dashed border-line-strong rounded-panel bg-panel text-muted data-[disabled]:opacity-50 data-[disabled]:cursor-default data-[dragging=true]:border-ink data-[dragging=true]:bg-surface data-[dragging=true]:text-ink${props.disabled ? '' : ' cursor-pointer hover:border-ink hover:text-ink'}`}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Icon name="upload" size={20} />
      <span id={textId} className="font-medium">
        Choose a file
      </span>
      <span id={labelId} className="sr-only">
        {props.label}
      </span>
      <input
        id={id}
        className="sr-only"
        type="file"
        accept={props.accept}
        aria-labelledby={`${labelId} ${textId}`}
        disabled={props.disabled}
        onChange={handleChange}
      />
    </label>
  );
}
